import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Spinner, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import UploadAgamiInterface from '../ViewUpload/UploadAgamiInterface'
import { getSpotTypes } from '../utils/helpers/supabase.helpers'
import { useSessionContext } from '../contexts/SessionContext'
import { usePageTitle } from '../hooks/toolkit'
import { useBucketInfo } from '../hooks/useBucketInfo'
import GoBack from '../components/GoBack'
import { useAgami } from '../hooks/useAgami'
import { GEOXP_AUDIO_BUCKET } from '../utils/constants/db.constants'

const DashboardAgamiDetail = () => {
  const { t } = useTranslation()
  usePageTitle(t('dashboardAgamiDetail.pageTitle'))

  const { id } = useParams()
  const { loading, user } = useSessionContext()

  const { isLoading: bucketLoading } = useBucketInfo({ bucket: GEOXP_AUDIO_BUCKET })

  const [availableSpotTypes, setAvailableSpotTypes] = useState<Record<string, string>>()

  const { agami, isLoading: agamiLoading, refetch } = useAgami({ agamiId: id || '' })

  const getAvailableSpotTypes = async () => {
    const { data } = await getSpotTypes()
    const types = (data as { id: string; label: string }[] | undefined)?.reduce(
      (acc: Record<string, string>, curr: { id: string; label: string }) => ({
        ...acc,
        [curr.id]: curr.label,
      }),
      {}
    )
    setAvailableSpotTypes(types)
  }

  useEffect(() => {
    if (loading || !user?.id) {
      return
    }
    getAvailableSpotTypes()
  }, [loading, user])

  const isLoading = useMemo(
    () => loading || bucketLoading || agamiLoading,
    [loading, bucketLoading, agamiLoading]
  )

  return (
    <div className="w-full flex flex-col py-4">
      <div className="flex items-center justify-center gap-2 self-start">
        <GoBack />
        <Typography variant="lead" className="text-primary" data-testid="agami-detail-title">
          {agami?.name ?? ''}
        </Typography>
      </div>

      {isLoading && (
        <div className="w-full my-10">
          <Spinner className="h-12 w-12 mx-auto" />
        </div>
      )}

      {!isLoading && !agami && (
        <div className="w-full my-10">
          <Typography>{t('dashboardAgamiDetail.notFound')}</Typography>
        </div>
      )}

      {!isLoading && availableSpotTypes && agami && (
        <UploadAgamiInterface availableSpotTypes={availableSpotTypes} agami={agami} refetchAgami={refetch} />
      )}
    </div>
  )
}

export default DashboardAgamiDetail
